import React, { useCallback, useEffect, useState } from 'react';
import { Button, CircularProgress, Paper, TextField, Typography } from '@material-ui/core';
import { fetchAuditRecipients, saveAuditRecipients, sendAuditReport } from '../actions/audit';

const AuditRecipients = ({ report = 'issues' }) => {
    const issues = report === 'issues';
    const titleId = 'audit-' + report + '-recipients-title';
    const [emails, setEmails] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setEmails((await fetchAuditRecipients(report)).join('\n'));
            setLoaded(true);
        } catch (_) { setError('Could not load report recipients. Please try again.'); }
        finally { setLoading(false); }
    }, [report]);
    useEffect(() => { load(); }, [load]);

    const save = async event => {
        event.preventDefault();
        if (!loaded || loading || saving || sending) return;
        setSaving(true);
        setError('');
        setMessage('');
        const recipients = emails.split(/[\s,;]+/).filter(Boolean);
        try {
            setEmails((await saveAuditRecipients(report, recipients)).join('\n'));
            setMessage('Report recipients saved.');
        } catch (failure) {
            setError(failure.response && failure.response.status === 400
                ? 'Please enter valid email addresses.'
                : 'Could not save report recipients. Please try again.');
        } finally { setSaving(false); }
    };

    const send = async () => {
        if (!loaded || loading || saving || sending) return;
        setSending(true);
        setError('');
        setMessage('');
        try {
            const result = await sendAuditReport(report);
            if (result.status === 'skipped') setError(result.message);
            else setMessage(result.message);
        } catch (failure) {
            setError((failure.response && failure.response.data && failure.response.data.message)
                || 'Could not send the report. Please try again.');
        } finally { setSending(false); }
    };

    return (
        <Paper component="section" style={{ padding: 20, height: '100%', boxSizing: 'border-box' }} aria-labelledby={titleId}>
            <Typography id={titleId} variant="h6">{issues ? 'Issues report recipients' : 'Upcoming slots report recipients'}</Typography>
            <Typography color="textSecondary" gutterBottom>
                {issues ? 'Recipients for clinic error reports.' : 'Recipients for next available day and time slot reports.'} Reports are emailed after the nightly audit.
            </Typography>
            {loading && <CircularProgress size={24} aria-label="Loading report recipients" />}
            {!loading && !loaded && <Button color="primary" onClick={load}>Retry loading recipients</Button>}
            <form onSubmit={save}>
                <TextField id={'audit-' + report + '-recipients'} label="Email recipients" multiline minRows={3} fullWidth variant="outlined"
                    value={emails} disabled={loading || saving || sending || !loaded}
                    onChange={event => { setEmails(event.target.value); setMessage(''); setError(''); }}
                    helperText="Enter one email per line, or separate with commas. Clear the list to remove all recipients."
                    style={{ marginTop: 12, marginBottom: 12 }} />
                <Button type="submit" variant="contained" color="primary" disabled={loading || saving || sending || !loaded}>
                    {saving ? 'Saving…' : 'Save recipients'}
                </Button>
                <Button type="button" variant="outlined" color="primary" onClick={send}
                    disabled={loading || saving || sending || !loaded} style={{ marginLeft: 8 }}>
                    {sending ? 'Sending…' : 'Send report'}
                </Button>
                <Typography color="textSecondary" variant="caption" display="block" style={{ marginTop: 8 }}>
                    Send uses saved recipients and the latest audit results. Save recipient changes first.
                </Typography>
            </form>
            {error && <Typography color="error" role="alert" style={{ marginTop: 12 }}>{error}</Typography>}
            {message && <Typography role="status" style={{ marginTop: 12 }}>{message}</Typography>}
        </Paper>
    );
};

export default AuditRecipients;
